import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import { usePublicDesignSettings } from '@/hooks/use-public-design-settings';
import { useSiteStylesState } from '@/stores/site-styles-store';

interface PublicThemeWrapperProps {
  children: ReactNode;
  className?: string;
  inert?: boolean;
}

const PublicThemeRootContext = createContext(false);

/**
 * Wraps content with the public site theme context.
 * Used in CMS editor previews to render sections with the site's public theme.
 */
export function PublicThemeWrapper({
  children,
  className,
  inert = false,
}: PublicThemeWrapperProps) {
  const isNested = useContext(PublicThemeRootContext);
  const { draftButtons, draftColors, draftFonts } = useSiteStylesState();
  const { buttons, themeVariables } = usePublicDesignSettings({
    buttons: draftButtons,
    colors: draftColors,
    fonts: draftFonts,
  });

  const wrapper = (
    <div
      className="public-theme bg-background text-foreground"
      data-theme-root={isNested ? undefined : ''}
      data-site-button-shape={isNested ? undefined : buttons?.shape}
      data-site-button-style={isNested ? undefined : buttons?.style}
      inert={inert}
      style={isNested ? undefined : themeVariables}
    >
      {className ? <div className={className}>{children}</div> : children}
    </div>
  );

  if (isNested) return wrapper;

  return (
    <PublicThemeRootContext.Provider value={true}>
      <div data-public-theme-root>{wrapper}</div>
    </PublicThemeRootContext.Provider>
  );
}
